import { type JSX, useCallback, useEffect, useState } from "react";

import { BenchApiError } from "../api/http";
import { type AttachedRepository, fetchAttachedRepositories } from "../api/repositories";

interface AttachedRepositoriesProps {
  /** Bumped by the parent after provisioning, to pull the list again. */
  refreshToken: number;
}

/**
 * What the Memory Service currently has attached.
 *
 * Answers the questions a tester has before touching the code operations or the webhook: which
 * repository ids exist, whether a push webhook was registered for each, and whether indexing has
 * caught up. Repositories are project-scoped, so this follows the project in the context bar.
 */
export function AttachedRepositories({ refreshToken }: AttachedRepositoriesProps): JSX.Element {
  const [items, setItems] = useState<AttachedRepository[] | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((signal?: AbortSignal): void => {
    setLoading(true);
    void fetchAttachedRepositories(signal)
      .then((response) => {
        setItems(response.items);
        setDetail(response.detail);
      })
      .catch((cause: unknown) => {
        setDetail(
          cause instanceof BenchApiError ? cause.message : `Could not read the list: ${String(cause)}`,
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshToken]);

  return (
    <section className="panel">
      <header className="panel__header">
        <div>
          <h2>Attached repositories</h2>
          <p className="muted">In the project currently in scope.</p>
        </div>
        <button type="button" onClick={() => load()} disabled={loading}>
          {loading ? "Reading…" : "Refresh"}
        </button>
      </header>

      {detail && (
        <div className="notice notice--warning">
          <strong>Could not list them.</strong> {detail}
        </div>
      )}

      {items !== null && items.length === 0 && !detail && (
        <p className="muted">
          Nothing attached yet. Create one above, or use <em>Sources &rarr; Attach a repository</em>{" "}
          for one that already exists elsewhere.
        </p>
      )}

      {items !== null && items.length > 0 && (
        <div className="table-scroll">
          <table className="repos">
            <thead>
              <tr>
                <th>Repository</th>
                <th>Id</th>
                <th>Branch</th>
                <th>Indexed</th>
                <th>Webhook</th>
                <th>Last commit</th>
              </tr>
            </thead>
            <tbody>
              {items.map((repository) => (
                <tr key={repository.id}>
                  <td>
                    <strong>{repository.full_name}</strong>
                    {repository.provider && <span className="muted"> · {repository.provider}</span>}
                    {repository.status && repository.status !== "attached" && (
                      <span className="flag flag--warn">{repository.status}</span>
                    )}
                  </td>
                  <td>
                    <code className="repos__id">{repository.id}</code>
                  </td>
                  <td>
                    <code>{repository.default_branch ?? "—"}</code>
                  </td>
                  <td>
                    {repository.files_indexed ?? "—"}
                    {(repository.files_pending ?? 0) > 0 && (
                      <span className="flag flag--warn">
                        {repository.files_pending} pending
                      </span>
                    )}
                  </td>
                  <td>
                    {repository.webhook_registered ? (
                      <span className="flag flag--on">registered</span>
                    ) : (
                      <span
                        className="flag flag--off"
                        title="Only a simulated push will reach the service for this one"
                      >
                        none
                      </span>
                    )}
                  </td>
                  <td>
                    <code>{shortCommit(repository.last_synced_commit)}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <p className="muted">
          Use an id under <em>Webhooks &rarr; Simulate an SCM push</em> or any code operation — the
          pickers there list these same repositories.
        </p>
      )}
    </section>
  );
}

function shortCommit(sha: string | null): string {
  return sha ? sha.slice(0, 10) : "—";
}
